import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { useAuth, LoginPage, RegisterPage, ProfilePage } from './pages/Auth';
import { ReservationsPage } from './pages/Reservations';
import { SocialPage } from './pages/Social';
import { CommunityPage } from './pages/Community';
import { LoyaltyPage } from './pages/Loyalty';
import { NotificationsPage } from './pages/Notifications';
import { GalleryPage } from './pages/Gallery';
import { ShopPage } from './pages/Shop';
import { TraiteurPage } from './pages/Traiteur';
import { community } from './api';
import { colors, radius, fonts } from './theme';
import SiamIntro from './components/SiamIntro';
import { IconReservations, IconSocial, IconCommunaute, IconFidelite, IconNotifications, IconGalerie, IconBoutique, IconMenu, IconProfil } from './components/Icons';

function NavItem({ to, icon, label, badge }: { to: string; icon: React.ReactNode; label: string; badge?: number }) {
  const loc = useLocation();
  const hashPath = '#' + loc.pathname;
  const active = hashPath === to || loc.pathname === to;
  return (
    <Link to={to} style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 16px', borderRadius: radius.sm,
      background: active ? 'rgba(201,169,110,0.15)' : 'transparent',
      borderRight: active ? '3px solid #C9A96E' : '3px solid transparent',
      color: active ? '#C9A96E' : '#A0A0A0',
      textDecoration: 'none', fontSize: 14, fontWeight: active ? 600 : 400,
      transition: 'all 0.2s',
    }}>
      {icon}
      <span>{label}</span>
      {badge && badge > 0 ? (
        <span style={{ background: '#E74C3C', color: '#fff', fontSize: 11, borderRadius: 10, padding: '1px 7px', marginLeft: 4 }}>{badge}</span>
      ) : null}
    </Link>
  );
}

function AppContent() {
  const { user, loading, login, register, logout, refreshUser } = useAuth();
  const [notifCount, setNotifCount] = useState(0);
  const [showIntro, setShowIntro] = useState(() => !sessionStorage.getItem('siam_intro_seen'));
  const [showPostLoginIntro, setShowPostLoginIntro] = useState(false);

  // Detect first login in this session to show post-login intro
  const [prevUser, setPrevUser] = useState<any>(null);
  useEffect(() => {
    if (user && !prevUser && !sessionStorage.getItem('siam_post_login_seen')) {
      setShowPostLoginIntro(true);
    }
    if (user !== prevUser) setPrevUser(user);
  }, [user]);

  useEffect(() => {
    if (user) {
      community.notifications().then((r: any) => {
        setNotifCount(r.data.filter((n: any) => !n.is_read).length);
      }).catch(() => {});
    }
  }, [user]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: colors.bg }}>
      <div style={{ color: colors.gold, fontSize: 28 }}>Le Siam</div>
    </div>
  );

  const handleIntroComplete = () => {
    sessionStorage.setItem('siam_intro_seen', '1');
    setShowIntro(false);
  };

  const handlePostLoginIntroComplete = () => {
    sessionStorage.setItem('siam_post_login_seen', '1');
    setShowPostLoginIntro(false);
  };

  if (showIntro) {
    return <SiamIntro onComplete={handleIntroComplete} duration={4} />;
  }

  if (showPostLoginIntro && user) {
    return <SiamIntro onComplete={handlePostLoginIntroComplete} duration={4} welcomeName={user.first_name || user.email} />;
  }

  if (!user) {
    return (
      <HashRouter>
        <Routes>
          <Route path="/register" element={<RegisterPage onRegister={register} />} />
          <Route path="*" element={<LoginPage onLogin={login} />} />
        </Routes>
      </HashRouter>
    );
  }

  return (
    <HashRouter>
      <div style={{ display: 'flex', minHeight: '100vh' }}>
        {/* Sidebar */}
        <nav style={{
          width: 240, background: '#1A1A3A', borderRight: '1px solid #2A2A4A',
          display: 'flex', flexDirection: 'column', position: 'fixed',
          top: 0, bottom: 0, left: 0, overflowY: 'auto',
        }}>
          <div style={{ padding: '20px 16px', borderBottom: '1px solid #2A2A4A', textAlign: 'center' }}>
            <img
              src={`${process.env.PUBLIC_URL}/lesiam_logo.jpg`}
              alt="Le Siam"
              style={{
                width: 90, height: 90, objectFit: 'cover',
                borderRadius: '50%',
                border: '3px solid #C9A96E',
                boxShadow: '0 0 20px rgba(201,169,110,0.5), 0 0 50px rgba(201,169,110,0.2)',
                marginBottom: 10,
              }}
            />
            <div style={{ fontSize: 28, fontWeight: 'bold', color: colors.gold }}>Le Siam</div>
            <div style={{ fontSize: 11, color: '#A0A0A0', marginTop: 2 }}>Bar Premium &bull; Traiteur Thai</div>
          </div>

          <div style={{ flex: 1, padding: '8px 8px' }}>
            <NavItem to="/" icon={<IconMenu />} label="Carte Traiteur" />
            <NavItem to="/shop" icon={<IconBoutique />} label="Boutique" />
            <NavItem to="/reservations" icon={<IconReservations />} label="Reservations" />
            <NavItem to="/social" icon={<IconSocial />} label="Social" />
            <NavItem to="/community" icon={<IconCommunaute />} label="Communaute" />
            <NavItem to="/loyalty" icon={<IconFidelite />} label="Fidelite" />
            <NavItem to="/notifications" icon={<IconNotifications />} label="Notifications" badge={notifCount} />
            <NavItem to="/gallery" icon={<IconGalerie />} label="Galerie" />
            <NavItem to="/profile" icon={<IconProfil />} label="Profil" />
          </div>

          <div style={{ padding: '12px 16px', borderTop: '1px solid #2A2A4A' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%', background: colors.gold,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#0F0F23', fontWeight: 'bold', fontSize: 14,
              }}>
                {(user.first_name?.[0] || user.email?.[0] || '?').toUpperCase()}
              </div>
              <div>
                <div style={{ color: '#F5F5F5', fontSize: 13, fontWeight: 600 }}>{user.first_name || user.email}</div>
                <div style={{ color: '#A0A0A0', fontSize: 11 }}>{user.loyalty_points} pts</div>
              </div>
            </div>
            <button onClick={logout} style={{
              width: '100%', padding: 8, background: 'transparent',
              border: '1px solid #E74C3C', color: '#E74C3C', borderRadius: radius.sm,
              cursor: 'pointer', fontSize: 12, fontFamily: fonts.body,
            }}>Deconnexion</button>
          </div>
        </nav>

        {/* Main Content */}
        <main style={{ flex: 1, marginLeft: 240, padding: 32, maxWidth: 900 }}>
          <Routes>
            <Route path="/" element={<TraiteurPage />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/reservations" element={<ReservationsPage />} />
            <Route path="/social" element={<SocialPage />} />
            <Route path="/community" element={<CommunityPage />} />
            <Route path="/loyalty" element={<LoyaltyPage user={user} />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/profile" element={<ProfilePage user={user} onRefresh={refreshUser} />} />
          </Routes>
        </main>
      </div>
    </HashRouter>
  );
}

export default function App() {
  return <AppContent />;
}